import React, {
  FunctionComponent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import { Analytics } from '@trello/atlassian-analytics';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { useFeatureFlag, featureFlagClient } from '@trello/feature-flag-client';
import { matches } from '@trello/locale';
import {
  AtlassianSwitcherPrefetchTrigger,
  defaultJoinableSitesFetch,
  createJoinableSitesProvider,
} from '@atlaskit/atlassian-switcher';
import { atlassianApiGateway } from '@trello/config';
import { AtlassianSwitcherVariations } from 'app/src/components/AtlassianAppSwitcher/AtlassianAppSwitcher';
import {
  OverlayType,
  setOverlay,
} from 'app/gamma/src/modules/state/ui/overlay';
import { setLocationToWacSoftware } from 'app/gamma/src/util/product-store';
import { usePopover, Popover } from '@trello/nachos';
import { AtlassianAppSwitcher } from 'app/src/components/AtlassianAppSwitcher';
import { useDispatch } from 'react-redux';
import { ProductKeys } from '@atlassiansox/product-store-react';

interface AtlassianAppSwitcherButtonProps {
  member: MemberModel | undefined;
}

const customJoinableSitesDataProvider = createJoinableSitesProvider(
  defaultJoinableSitesFetch(`${atlassianApiGateway}gateway/api/growth`),
);

export const AtlassianAppSwitcherButton: FunctionComponent<AtlassianAppSwitcherButtonProps> = ({
  member,
}) => {
  const locale = member?.prefs?.locale;
  const isAaMastered = member?.isAaMastered;
  const switcherVariation = useFeatureFlag(
    'a4aa.atlassian-app-switcher',
    AtlassianSwitcherVariations.NOT_ENROLLED,
  );
  const switcherJoinVariation = useFeatureFlag(
    'aa4a.atlassian-switcher-joinable-sites',
    AtlassianSwitcherVariations.NOT_ENROLLED,
  );

  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const isJoinEnabled =
    switcherJoinVariation === AtlassianSwitcherVariations.EXPERIMENT;

  const onShow = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianSwitcherHeaderButton',
      source: 'atlassianSwitcherHeaderButton',
    });
    trackUe({
      category: Noun.ATLASSIAN_APP_SWITCHER,
      verb: Verb.CLICKS,
      directObj: Noun.ATLASSIAN_APP_SWITCHER_BUTTON,
    });
  }, []);

  const { popoverProps, triggerRef, toggle, hide } = usePopover<
    HTMLButtonElement
  >({
    onShow,
  });

  useEffect(() => {
    const checkShowAppSwitcher = async () => {
      // If the user isn't enrolled, do nothing.
      if (switcherVariation === AtlassianSwitcherVariations.NOT_ENROLLED) {
        return;
      }
      // If the user has an Atlassian Account and has an English locale...
      if (isAaMastered && typeof locale === 'string' && matches('en', locale)) {
        // Fire the exposure event.
        featureFlagClient.getTrackedVariation(
          'a4aa.atlassian-app-switcher',
          AtlassianSwitcherVariations.NOT_ENROLLED,
        );
        // Only actually show the switcher to users in the "experiment" variation.
        if (switcherVariation === AtlassianSwitcherVariations.EXPERIMENT) {
          setShowAppSwitcher(true);
        }
      }
    };

    checkShowAppSwitcher();
  }, [switcherVariation, isAaMastered, locale]);

  const dispatch = useDispatch();
  const triggerXFlow = useCallback(
    (productKey: ProductKeys) => {
      hide();
      dispatch(
        setOverlay({ overlayType: OverlayType.ProductStore, productKey }),
      );
    },
    [dispatch, hide],
  );

  return showAppSwitcher ? (
    <>
      <AtlassianSwitcherPrefetchTrigger
        joinableSitesDataProvider={
          isJoinEnabled ? customJoinableSitesDataProvider : null
        }
      >
        <HeaderButton
          icon={IconName.AppSwitcher}
          onClick={toggle}
          ref={triggerRef}
          testId={HeaderTestIds.AtlassianAppSwitcher}
        />
      </AtlassianSwitcherPrefetchTrigger>
      <Popover
        {...popoverProps}
        title="More from Atlassian"
        testId={HeaderTestIds.AtlassianAppSwitcher}
      >
        <AtlassianAppSwitcher
          triggerXFlow={triggerXFlow}
          onDiscoverMoreClicked={setLocationToWacSoftware}
          joinableSitesDataProvider={customJoinableSitesDataProvider}
        />
      </Popover>
    </>
  ) : null;
};
